import {
  AnnouncementMessage,
  ICreditClassData,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import { DismissableBannerProps } from '@/components/blocks/DismissableBannerBlock';
import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import creditTrackerEndpoints from '../api/client/creditTracker';
import userLicensesEndpoints from '../api/client/userLicenses';

export interface CreditTrackerState {
  isLoading: boolean;
  request: UserTrackerDataRequest[] | null;
  error: FetchBaseQueryError | SerializedError | null;
  selectedRegion?: Record<number, UserTrackerData>;
  trackers: Record<number, Array<UserTrackerData>>;
  providers: Array<string>;
  announcements: Record<string, Array<AnnouncementMessage>>;
  creditTypes: Array<ICreditClassData>;
  globalBanners?: Record<string, DismissableBannerProps[] | null> | null;
}

const initialState: CreditTrackerState = {
  isLoading: false,
  error: null,
  request: null,
  selectedRegion: undefined,
  trackers: {},
  providers: [],
  creditTypes: [],
  announcements: {}
};

export const creditTrackerSlice = createSlice({
  name: 'creditTracker',
  initialState,
  reducers: {
    creditTrackerLoading: (state, payload: PayloadAction<boolean>) => {
      state.isLoading = payload.payload;
    },
    creditTrackerDataSuccess: (
      state,
      action: PayloadAction<{
        request: UserTrackerDataRequest[];
        response: Array<Record<string, Array<UserTrackerData>>> | null;
      }>
    ) => {
      state.isLoading = false;
      for (const trackers of action.payload.response ?? []) {
        for (const key in trackers) {
          const parsedKey = parseInt(key);
          state.trackers[parsedKey] = trackers[parsedKey];
        }
      }
      state.request = action.payload.request;
      state.error = null;
    },
    creditTrackerSetSelectedRegion: (state, action: PayloadAction<UserTrackerData | undefined>) => {
      updateSelectedRegion(state, action.payload);
      state.isLoading = false;
    },
    creditTrackerGetCreditTypesSuccess: (state, action: PayloadAction<Array<ICreditClassData>>) => {
      state.creditTypes = action.payload;
      state.isLoading = false;
    },
    creditTrackerFailure: (state, action: PayloadAction<FetchBaseQueryError | SerializedError>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    creditTrackerProvidersSuccess: (state, action: PayloadAction<Array<string>>) => {
      state.providers = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    creditTrackerSetGlobalBanners: (
      state,
      action: PayloadAction<Record<string, DismissableBannerProps[]>>
    ) => {
      state.globalBanners ??= {};

      for (const key in action.payload) {
        state.globalBanners[key] = action.payload[key];
      }
    },
    creditTrackerRemoveGlobalBanners: (state, action: PayloadAction<string>) => {
      delete state.globalBanners?.[action.payload];
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      creditTrackerEndpoints.endpoints.getCreditTracker.matchFulfilled,
      (state, action) => {
        state.isLoading = false;
        for (const trackers of action.payload?.trackerUsers ?? []) {
          for (const key in trackers) {
            const parsedKey = parseInt(key);
            state.trackers[parsedKey] = trackers[parsedKey];
          }
        }
        state.request = [...(action.meta.arg.originalArgs ?? [])];
        state.error = null;

        const individualSk = action.meta.arg.originalArgs?.[0]?.individualSk ?? -1;
        const currentSelectedRegion = state.selectedRegion?.[individualSk];
        const updatedSelectedRegion = action.payload?.trackerUsers
          ?.find(a => !!a?.[individualSk?.toString() ?? ''])
          ?.[
            individualSk?.toString() ?? ''
          ]?.find(a => a.creditRegion_SK === currentSelectedRegion?.creditRegion_SK);

        if (updatedSelectedRegion) {
          updateSelectedRegion(state, updatedSelectedRegion);
        }
      }
    );
    builder.addMatcher(
      creditTrackerEndpoints.endpoints.getManyAnnouncementMessages.matchFulfilled,
      (state, action) => {
        for (const key in action.payload) {
          if (key?.trim()?.length) {
            state.announcements[key] = action.payload[key];
          }
        }
        state.isLoading = false;
      }
    );
    builder.addMatcher(userLicensesEndpoints.endpoints.deleteLicense.matchFulfilled, () =>
      resetState()
    );
    builder.addMatcher(userLicensesEndpoints.endpoints.postLicense.matchFulfilled, () =>
      resetState()
    );
    builder.addMatcher(userLicensesEndpoints.endpoints.updateLicense.matchFulfilled, () =>
      resetState()
    );
  }
});

function resetState(): CreditTrackerState {
  return {
    ...initialState
  };
}

function updateSelectedRegion(state: CreditTrackerState, data: UserTrackerData | undefined) {
  const val = { ...data } as UserTrackerData;
  if (val.customerId) {
    if (state.selectedRegion) state.selectedRegion[val.customerId] = val;
    else state.selectedRegion = { [val.customerId]: val };
  }
}

export const {
  creditTrackerLoading,
  creditTrackerDataSuccess,
  creditTrackerFailure,
  creditTrackerGetCreditTypesSuccess,
  creditTrackerSetSelectedRegion,
  creditTrackerProvidersSuccess,
  creditTrackerSetGlobalBanners,
  creditTrackerRemoveGlobalBanners
} = creditTrackerSlice.actions;

export default creditTrackerSlice.reducer;
